import { useDispatch, useSelector } from 'react-redux';
import { canUserEditProjectSelector } from '../../../user/user-selectors';
import { shouldPostFirstUpdate } from '../../project-model';
import GenericMessage from '../../../components/info-messages/GenericMessage';
import { getLatestProjectUpdate } from '../../project-update-reducer';
import { useProject } from '../../project-hooks/useProject';
import TrackEventLink from '../../../components/track-event-link/TrackEventLink';
import { Subtitle, Title } from '../../../components/typography/Heading';
import { isMobile } from '../../../utils';
import { trackEvent } from '../../../support-and-tracking/analytics-actions';
import { openProjectUpdatesModal } from '../../project-updates-actions';
import styles from './update-notification.module.less';
import I18nText from '../../i18n/I18nText';
import { LATEST_PROJECT_UPDATE_TEXT_KEY } from '../../i18n/project-i18n-model';
import { getUserAgent } from '../../../utils/browserDetector';

const UpdateNotificationMessage = ({ useModal, to, className, ...props }) => {
    const dispatch = useDispatch();
    const content = (
        <>
            <GenericMessage
                icon="notification2"
                containerStyle={styles.notification}
                iconContainerClass={styles.iconContainerClass}
                iconStyle={styles.iconStyle}
                textStyle={styles.textStyle}
                {...props}
            />
            <i className={`fas fa-chevron-right ${styles.updateIcon}`} />
        </>
    );
    if (useModal) {
        return (
            <div
                className={[className, styles.wrapper].join(' ')}
                onClick={() => {
                    dispatch(openProjectUpdatesModal());
                    trackEvent('project-latest-activity', 'click', { 'project-id': props.projectId });
                }}
            >
                {content}
            </div>
        );
    } else {
        return (
            <TrackEventLink to={to} className={[className, styles.wrapper].join(' ')} preventScrollReset={true}>
                {content}
            </TrackEventLink>
        );
    }
};

const UpdateNotification = ({ className = '' }) => {
    const project = useProject();
    const canUserEdit = useSelector((state) => canUserEditProjectSelector(state, project.id));
    const lastUpdate = useSelector((state) => getLatestProjectUpdate(state, project.id));
    const userAgent = useSelector((state) => getUserAgent(state));
    const useModal = isMobile(userAgent);

    if (canUserEdit && !lastUpdate) {
        if (shouldPostFirstUpdate(project)) {
            return (
                <UpdateNotificationMessage to={`/project/${project.id}/update/new`} className={className}>
                    <Subtitle className={styles.cta}>Legg ut en oppdatering på spleisen</Subtitle>
                </UpdateNotificationMessage>
            );
        }
    }

    if (!lastUpdate) {
        return null;
    }

    const receiverName = project.receiverInformation?.name || 'mottaker';

    return (
        <UpdateNotificationMessage
            useModal={useModal}
            projectId={project?.id}
            to={`/project/${project.id}/update`}
            className={className}
        >
            <Title>
                <I18nText textKey={LATEST_PROJECT_UPDATE_TEXT_KEY} />
            </Title>
            <p className={styles.link}>«{lastUpdate.from_receiver ? `Fra ${receiverName}` : lastUpdate.title}»</p>
        </UpdateNotificationMessage>
    );
};

export default UpdateNotification;
